import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  REACT_APP_POOL_TIME,
  REACT_APP_ENABLE_SERVERSENTEVENT,
} from '../config';
import { MarkMessageRead } from '../services/chat';
import {
  DeleteConversation,
  GetMessagesForConversations,
  DeleteManualStaffGroup,
  setConversationState,
  GetOrCreateConversationByContactInformation,
  DecryptMessageForConversation,
} from '../services/conversations';
import { getUserGuid } from '../storage';
import { CompareLists } from '../utils';
import { useAuth } from './auth';
import { useConversation } from './conversation';
import {
  EncryptFile,
  RsaDecryptor,
  DecryptFile,
  EncryptMessage,
} from '../services/e2ee';
import { getE2EEncryptionEnabledFlag } from '../storage/index';

const ChatMessagesContext = createContext();
const ChatMessagesProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState([]);
  const [conversation, setConversation] = useState();
  const [group, setGroup] = useState();
  const [oldConversation, setOldConversation] = useState();
  const [cancelPool, setCancelPool] = useState(false);
  const [hasUpdate, setHasUpdate] = useState(0);
  const { portal } = useAuth();
  const userGuid = getUserGuid();
  const {
    setOpenInfo,
    reloadConversations,
    conversations,
    GetUserConversations,
    conversationDecryptedKey,
    conversationDecryptedIV,
    getDecryptionParams,
  } = useConversation();
  const [selectedPage, setSelectedPage] = useState(false);
  const [openChat, setOpenChat] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const isE2EEnabled = getE2EEncryptionEnabledFlag();

  // Reusable fetch function
  const fetchMessages = async (shouldCheckUpdate = true) => {
    if (
      !conversation ||
      cancelPool ||
      !portal ||
      document.visibilityState !== 'visible'
    ) {
      return;
    }

    try {
      console.log('Fetching messages from API...');
      const data = await GetMessagesForConversations({
        Guid: conversation.Guid,
        maxNumberMessages: 40,
        ...(isE2EEnabled && {
          conversationDecryptedKey,
          conversationDecryptedIV,
        }),
      });

      if (data.GetMessagesForConversationResult) {
        // for (const message of data.orderedMessages) {
        //   if (
        //     !message.IsRead &&
        //     `${message.Sender.StaffGuid}` !== `${userGuid}`
        //   ) {
        //     await MarkMessageRead({ messageGuid: message.Guid });
        //     if (REACT_APP_ENABLE_SERVERSENTEVENT === 'true') {
        //       // TODO: need  to call automatically or under discussion(resposible for api cout and call)
        //       GetUserConversations();
        //     }
        //   }
        // }

        const unreadMessages = data.orderedMessages.filter(
          (message) =>
            !message.IsRead && `${message.Sender.StaffGuid}` !== `${userGuid}`
        );

        if (unreadMessages.length > 0) {
          await Promise.all(
            unreadMessages.map((message) =>
              MarkMessageRead({ messageGuid: message.Guid })
            )
          );
          if (REACT_APP_ENABLE_SERVERSENTEVENT === 'true') {
            GetUserConversations(); // Called once, after all are marked read
          }
        }

        if (
          shouldCheckUpdate &&
          CompareLists({ list1: messages, list2: data.orderedMessages })
        ) {
          setHasUpdate((prev) => (prev === 0 ? 1 : 0));
        }

        setMessages(data.orderedMessages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Polling logic if SSE is disabled
  useEffect(() => {
    if (REACT_APP_ENABLE_SERVERSENTEVENT === 'false') {
      const interval = setInterval(
        () => fetchMessages(true),
        REACT_APP_POOL_TIME
      );
      return () => clearInterval(interval);
    } else {
      fetchMessages(true); // Run once on mount if SSE is enabled
    }
  }, [conversation, cancelPool, portal]);

  // Separate hook just for `hasUpdate`
  useEffect(() => {
    console.log('hasUpdate', hasUpdate);
    if (REACT_APP_ENABLE_SERVERSENTEVENT === 'true') {
      fetchMessages(false);
    }
  }, [hasUpdate]);

  const addMessageManually = ({ message }) => {
    setMessages((state) => [...state, message]);
  };

  const deleteConversation = async ({ data }) => {
    try {
      await DeleteManualStaffGroup({
        staffGroup: data,
      });
      const cs = await reloadConversations();
      console.log(cs);
      if (cs && cs.length > 0) {
        setConversation(cs[0]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const setConversationArchive = async (guid, isArchive) => {
    try {
      await setConversationState(
        {
          Guid: guid,
        },
        isArchive
      );
      const cs = await reloadConversations();
      console.log(cs);
      if (cs && cs.length > 0) {
        setConversation(cs[0]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const createGroupConversation = async (group) => {
    try {
      const response = await GetOrCreateConversationByContactInformation({
        contactInformation: {
          Name: group.Name,
          Priority: group.Priority,
          StaffGuid: group.GroupGuid,
        },
      });
      setSelecetedConversation({
        isGroup: true,
        ...response.conversation,
      });
    } catch (error) {
      // console.log(error)
    }
  };

  const setSelecetedConversation = async (conversation, dontCloseInfo) => {
    try {
      setIsLoading(true);

      if (portal) {
        if (oldConversation && !conversation.isGroup) {
          await GetMessagesForConversations({
            Guid: oldConversation.Guid,
            maxNumberMessages: 1,
            ...(isE2EEnabled && {
              conversationDecryptedKey,
              conversationDecryptedIV,
            }),
          }).then(async (data) => {
            if (data.GetMessagesForConversationResult) {
              if (data.orderedMessages.length === 0) {
                DeleteConversation({ Guid: oldConversation.Guid });
              }
            }
          });
        }
        setConversation(conversation);
        setOldConversation(conversation);
        if (!dontCloseInfo) {
          //setOpenInfo(false);
          let listUnreadMessages = [];
          setCancelPool(false);
          if (conversation) {
            await GetMessagesForConversations({
              Guid: conversation.Guid,
              maxNumberMessages: 40,
              ...(isE2EEnabled && {
                conversationDecryptedKey,
                conversationDecryptedIV,
              }),
            }).then(async (data) => {
              if (data.GetMessagesForConversationResult) {
                data.orderedMessages.forEach(async (message) => {
                  if (
                    !message.IsRead &&
                    `${message.Sender.StaffGuid}` !== `${userGuid}`
                  ) {
                    listUnreadMessages.push(message);
                    await MarkMessageRead({ messageGuid: message.Guid });
                  }
                });
                setMessages(data.orderedMessages);
                //TODO: Need to remove
                // setHasUpdate(hasUpdate == 0 ? 1 : 0);
              }
            });
            setUnreadMessages(listUnreadMessages);
            if (REACT_APP_ENABLE_SERVERSENTEVENT === 'true') {
              // TODO: need  to call automatically or under discussion(resposible for api cout and call)
              await GetUserConversations();
            }
          }
        }
      }
    } catch (error) {
      // console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearConversation = () => {
    setConversation();
  };

  const setSelectedGroup = (group) => {
    try {
      if (portal) {
        setGroup(group);
      }
    } catch (error) {
      // console.log(error);
    }
  };

  const encryptChatMessage = async (message, conversationGuid) => {
    try {
      const decryptionParams = getDecryptionParams(conversationGuid);

      if (decryptionParams) {
        const data = await EncryptMessage(
          message,
          decryptionParams.keyObj.Key,
          decryptionParams.ivObj.IV
        );
        return data;
      } else {
        return message;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const decryptPollMessage = async (message, conversationGuid) => {
    try {
      const data = await DecryptMessageForConversation(
        message,
        conversationDecryptedKey.find((item) => item.Guid === conversationGuid),
        conversationDecryptedIV.find((item) => item.Guid === conversationGuid)
      );
      return data;
    } catch (error) {
      console.log(error);
    }
  };

  const encryptAttachment = async (file, attachmentKey, attachmentIv) => {
    try {
      if (!attachmentKey || !attachmentIv) return file;
      const base64File = await getFileAsBase64(file);
      const decryptedAttachmentKey = await RsaDecryptor(attachmentKey);
      const decryptedAttachmentIv = await RsaDecryptor(attachmentIv);

      const data = await EncryptFile(
        base64File,
        decryptedAttachmentKey,
        decryptedAttachmentIv
      );
      console.log(data);
      return data;
    } catch (error) {
      console.log(error);
    }
  };

  const decryptAndCreateBlobUrl = async (
    attachmentPath,
    key,
    iv,
    mimeType,
    name
  ) => {
    try {
      const response = await fetch(attachmentPath);
      if (!response.ok) throw new Error('Failed to download');

      const text = await response.text();

      const decryptedKey = await RsaDecryptor(key);
      const decryptedIv = await RsaDecryptor(iv);

      console.log('decryptedKey', decryptedKey);
      console.log('decryptedIv', decryptedIv);

      const decryptedBase64 = await DecryptFile(
        text,
        decryptedIv,
        decryptedKey
      );

      const byteArray = Uint8Array.from(atob(decryptedBase64), (c) =>
        c.charCodeAt(0)
      );
      const fileBlob = new Blob([byteArray], { type: mimeType });
      const url = URL.createObjectURL(fileBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Decryption error', error);
      return null;
    }
  };

  const getFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result.split(',')[1]; // remove data:image/...;base64,
        resolve(result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  return (
    <ChatMessagesContext.Provider
      value={{
        messages,
        setMessages,
        addMessageManually,
        conversation,
        setConversation,
        setSelecetedConversation,
        setCancelPool,
        hasUpdate,
        unreadMessages,
        deleteConversation,
        setConversationArchive,
        setSelectedGroup,
        group,
        clearConversation,
        selectedPage,
        setSelectedPage,
        createGroupConversation,
        openChat,
        setOpenChat,
        setHasUpdate,
        encryptChatMessage,
        decryptPollMessage,
        isLoading,
        encryptAttachment,
        decryptAndCreateBlobUrl,
      }}
    >
      {children}
    </ChatMessagesContext.Provider>
  );
};

function useChatMessages() {
  const context = useContext(ChatMessagesContext);
  if (!context) {
    throw new Error('useChatMessages must be used within a AppProvider');
  }
  return context;
}
export { ChatMessagesProvider, ChatMessagesContext, useChatMessages };
